import React from 'react';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { Defect } from '../../../models/defects';
import { Divider, Label, SectionHeader, Text } from '../../CommonStyledComponents/CommonStyledComponents';
import { DataValue } from '../../../utils/updateLocalDataObject';
import FileViewer from '../../TFFileViewer/fileViewer';
import ResolutionDetails from '../../WorkPackDrawer/Components/ResolutionDetails';
import { SignatureReason } from '../../../models/signatures';
import { getSignature } from '../../../utils/signatureUtils';
import { getLastResolutionEvent } from '../../../utils/mxEventUtils';
import ViewSignatureRow from './ViewSignatureRow';
import ViewDefectDrawerDeferralOptions from './ViewDefectDrawerDeferralOptions';
import ViewLimitations from './ViewLimitations';
import ViewDefectAdditionalInformation from './ViewDefectAdditionalInformation';
import ViewRectificationExtension from './ViewRectificationExtension';
import ApprovalRelease from './ApprovalRelease';
import ViewDefectApprovalReleaseRequired from './ViewDefectApprovalReleaseRequired';

interface ViewDefectDrawerDetailsProps {
  defectData: Defect;
  editResolved: boolean;
  updateDefectData: (changes: { value: DataValue; key: string }[]) => void;
  requiredSettings: {
    defectRename: string;
    reportedByTypeEnabled: boolean;
    stageOfFlightEnabled: boolean;
    stageOfFlightText: string;
    eropsRename: string;
    melNameOverride: string;
    cdlNameOverride: string;
    nefNameOverride: string;
    casNameOverride: string;
    otherNameOverride: string;
    showRichTextMel: boolean;
    troubleShootingStepsRename: string;
    deferralApprovalReferenceRename: string;
    part145ApprovalNumberRename: string;
    engineerRename: string;
    engineerLicenceNumberRename: string;
    dateFormat: string;
    resolutionRename: string;
    currentLoginId: string;
    currentUserId: string;
    approvalReleaseName: string;
    approvalReleaseMode: string;
    isApprovalReleaseModeFeatureFlagEnabled: boolean;
  };
  aircraftId: string;
}

const GridSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin: 20px 0 0;
  @media (max-width: 451px) {
    grid-template-columns: 1fr;
  }
`;

const StyledTextarea = styled.textarea`
  border-radius: 4px;
  border-color: rgba(36, 45, 65, 0.1);
  padding: 8px;
`;

const ViewDefectDrawerDetails: React.FC<ViewDefectDrawerDetailsProps> = ({
  defectData,
  editResolved,
  updateDefectData,
  requiredSettings,
  aircraftId,
}) => {
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();

  const handleResolutionSave = () => {
    queryClient.invalidateQueries(['defectDrawer']);
  };

  const canViewApprovalReleaseRequired =
    requiredSettings.approvalReleaseMode === 'after_each_approval_release_required';

  const lastResolution = getLastResolutionEvent(defectData?.mx_events);

  return (
    <div>
      <SectionHeader data-testid="ViewDefectDrawerDetails--Header">
        {formatMessage({ id: 'form.labels.defectDetails' }, { field: requiredSettings.defectRename })}
      </SectionHeader>
      <GridSection>
        <div data-testid="ViewDefectDrawerDetails--DetailsSection">
          <Label>{formatMessage({ id: 'text.details' })}</Label>
          {editResolved ? (
            <StyledTextarea
              id="details"
              name="details"
              data-testid="EditDefectDrawerDetails--TextArea"
              cols={30}
              rows={5}
              onChange={(e): void => {
                updateDefectData([{ value: e.target.value, key: 'details' }]);
              }}
              value={defectData?.details}
            />
          ) : (
            <Text>{defectData?.details || '-'}</Text>
          )}
        </div>
        <div data-testid="ViewDefectDrawerDetails--AttachmentsSection">
          <Label>{formatMessage({ id: 'text.attachments' })}</Label>
          <FileViewer
            attachments={defectData?.attachments}
            uploaderVisible={false}
            allowDelete={false}
            disableAddition
          />
        </div>
        {requiredSettings.reportedByTypeEnabled ? (
          <div data-testid="ViewDefectDrawerDetails--ReportedByTypeSection">
            <Label>{`${formatMessage({ id: 'text.reportedBy' })}:`}</Label>
            <Text>{defectData?.reported_by_type || '-'}</Text>
          </div>
        ) : null}
        {requiredSettings.stageOfFlightEnabled ? (
          <div data-testid="ViewDefectDrawerDetails--StageOfFlightSection">
            <Label>{requiredSettings.stageOfFlightText}</Label>
            <Text>{defectData?.stage_of_flight || '-'}</Text>
          </div>
        ) : null}
      </GridSection>
      <ViewSignatureRow
        partialDefectData={{
          person: defectData?.reporter,
          date: defectData?.raised_at,
          signatureData: getSignature(defectData?.signatures, SignatureReason.STANDARD),
        }}
        dateFormat={requiredSettings.dateFormat}
        type={`${formatMessage({ id: 'text.reportedBy' })}:`}
      />
      <Divider />
      <ViewDefectDrawerDeferralOptions
        defectData={defectData} requiredSettings={requiredSettings} aircraftId={aircraftId} />
      <ViewLimitations defectData={defectData} eropsRename={requiredSettings.eropsRename} />
      <ViewDefectAdditionalInformation
        defectData={defectData}
        requiredSettings={{
          troubleShootingStepsRename: requiredSettings.troubleShootingStepsRename,
          deferralApprovalReferenceRename: requiredSettings.deferralApprovalReferenceRename,
          part145ApprovalNumberRename: requiredSettings.part145ApprovalNumberRename,
          engineerRename: requiredSettings.engineerRename,
          engineerLicenceNumberRename: requiredSettings.engineerLicenceNumberRename,
        }}
      />
      {defectData?.deferred && defectData?.display_data?.category !== 'A' ? (
        <ViewRectificationExtension
          rectificationIntervalExtension={defectData?.rectification_interval_extension}
          dateFormat={requiredSettings.dateFormat}
        />
      ) : null}
      {requiredSettings.isApprovalReleaseModeFeatureFlagEnabled && canViewApprovalReleaseRequired ? (
        <ViewDefectApprovalReleaseRequired
          approvalReleaseName={requiredSettings.approvalReleaseName}
          approvalReleaseRequired={defectData?.approval_release_required}
        />
      ) : null}
      {defectData?.deferred_by?.id ? (
        <ViewSignatureRow
          partialDefectData={{
            person: defectData?.deferred_by,
            date: defectData?.deferred_at || defectData?.raised_at,
            signatureData: getSignature(defectData?.signatures, SignatureReason.DEFERRAL),
          }}
          dateFormat={requiredSettings.dateFormat}
          type={formatMessage({ id: 'text.deferredBy' })}
        />
      ) : null}
      {defectData?.edited_by?.id ? (
        <ViewSignatureRow
          partialDefectData={{
            person: defectData?.edited_by,
            date: defectData?.edited_date,
            signatureData: getSignature(defectData?.signatures, SignatureReason.APPROVAL),
          }}
          dateFormat={requiredSettings.dateFormat}
          type={formatMessage({ id: 'text.editedBy' })}
        />
      ) : null}
      {lastResolution ? (
        <>
          <Divider />
          <ResolutionDetails
            release={lastResolution}
            poNum={lastResolution.workpack_reference}
            noCard
            handleResolutionSave={handleResolutionSave}
          />
        </>
      ) : null}
      {defectData?.approval_release && (
        <>
          <Divider />
          <ApprovalRelease
            approvalRelease={defectData?.approval_release}
            approvalReleaseName={requiredSettings.approvalReleaseName}
          />
        </>
      )}
    </div>
  );
};

export default ViewDefectDrawerDetails;
